import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { useState } from "react";
import {
  canvasToolbarButtonVariants,
  canvasToolbarSubmenuPanelClassName,
  canvasToolbarSubmenuRowActiveClassName,
  canvasToolbarSubmenuRowClassName,
} from "../../components/toolbar/canvasToolbarStyles";
import {
  LineStrokeStyle,
  ShapeStrokeStyle,
} from "../../hooks/useCanvasShapeAndEdgeStyle";
import { BorderStyleMenuIcon } from "./BorderStyleIcons";
import {
  CanvasStrokeTypePreview,
  CanvasStrokeWidthPreview,
} from "./CanvasToolbarGraphics";

export interface LineStyle {
  strokeStyle: LineStrokeStyle;
  strokeWidth: number;
}

export interface ShapeLineStyle {
  strokeStyle: ShapeStrokeStyle;
  strokeWidth: number;
}

const LINE_STYLE_OPTIONS: {
  value: LineStrokeStyle;
  label: string;
}[] = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
];

const SHAPE_STYLE_OPTIONS: {
  value: ShapeStrokeStyle;
  label: string;
}[] = [...LINE_STYLE_OPTIONS, { value: "none", label: "None" }];

const WEIGHT_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Thin" },
  { value: 2, label: "Medium" },
  { value: 4, label: "Heavy" },
];

interface LineStylePickerBaseProps {
  onOpenChange?: (open: boolean) => void;
  tooltip?: string;
  popoverSide?: "top" | "bottom" | "left" | "right";
  popoverSideOffset?: number;
  triggerClassName?: string;
}

interface LineStylePickerProps extends LineStylePickerBaseProps {
  includeNone?: false;
  value: LineStyle;
  onChange: (style: LineStyle) => void;
}

interface ShapeLineStylePickerProps extends LineStylePickerBaseProps {
  includeNone: true;
  value: ShapeLineStyle;
  onChange: (style: ShapeLineStyle) => void;
}

type LineStylePickerPropsUnion =
  | LineStylePickerProps
  | ShapeLineStylePickerProps;

interface LineStylePickerContentProps {
  value: ShapeLineStyle;
  onChange: (style: ShapeLineStyle) => void;
  styleOptions: readonly { value: ShapeStrokeStyle; label: string }[];
  onOpenChange?: (open: boolean) => void;
  tooltip: string;
  popoverSide?: "top" | "bottom" | "left" | "right";
  popoverSideOffset?: number;
  triggerClassName?: string;
}

function LineStylePickerContent({
  value,
  onChange,
  styleOptions,
  onOpenChange,
  tooltip,
  popoverSide = "top",
  popoverSideOffset = 14,
  triggerClassName,
}: LineStylePickerContentProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(canvasToolbarButtonVariants(), triggerClassName)}
              onClick={(e) => e.stopPropagation()}
            >
              <BorderStyleMenuIcon />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        align="center"
        side={popoverSide}
        sideOffset={popoverSideOffset}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={canvasToolbarSubmenuPanelClassName}>
          {styleOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                onChange({
                  strokeStyle: opt.value,
                  strokeWidth: value.strokeWidth,
                })
              }
              className={cn(
                canvasToolbarSubmenuRowClassName,
                value.strokeStyle === opt.value &&
                  canvasToolbarSubmenuRowActiveClassName,
              )}
            >
              <CanvasStrokeTypePreview
                strokeStyle={opt.value}
                active={value.strokeStyle === opt.value}
              />
              <span>{opt.label}</span>
            </button>
          ))}

          <div className="mx-0.5 my-0.5 h-px bg-n-10" />

          {WEIGHT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                onChange({
                  strokeStyle: value.strokeStyle,
                  strokeWidth: opt.value,
                })
              }
              className={cn(
                canvasToolbarSubmenuRowClassName,
                value.strokeWidth === opt.value &&
                  canvasToolbarSubmenuRowActiveClassName,
              )}
            >
              <CanvasStrokeWidthPreview
                width={opt.value}
                active={value.strokeWidth === opt.value}
              />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface StrokeWidthPickerProps extends LineStylePickerBaseProps {
  value: number;
  onChange: (strokeWidth: number) => void;
}

/** Weight-only picker — freehand ink has no dash patterns to offer. */
export function StrokeWidthPicker({
  value,
  onChange,
  onOpenChange,
  tooltip = "Stroke width",
  popoverSide = "top",
  popoverSideOffset = 14,
  triggerClassName,
}: StrokeWidthPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={tooltip}
              className={cn(canvasToolbarButtonVariants(), triggerClassName)}
              onClick={(e) => e.stopPropagation()}
            >
              <CanvasStrokeWidthPreview width={value} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        align="center"
        side={popoverSide}
        sideOffset={popoverSideOffset}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={canvasToolbarSubmenuPanelClassName}>
          {WEIGHT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                handleOpenChange(false);
              }}
              className={cn(
                canvasToolbarSubmenuRowClassName,
                value === opt.value && canvasToolbarSubmenuRowActiveClassName,
              )}
            >
              <CanvasStrokeWidthPreview
                width={opt.value}
                active={value === opt.value}
              />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Stroke style + weight picker.
 * Style rows: fixed 1.6px weight so dash patterns are legible at every option.
 * Weight rows: always solid so thickness differences are clear.
 */
export function LineStylePicker(props: LineStylePickerPropsUnion) {
  const {
    onOpenChange,
    tooltip = "Stroke",
    popoverSide,
    popoverSideOffset,
    triggerClassName,
  } = props;

  if (props.includeNone) {
    return (
      <LineStylePickerContent
        value={props.value}
        onChange={props.onChange}
        styleOptions={SHAPE_STYLE_OPTIONS}
        onOpenChange={onOpenChange}
        tooltip={tooltip}
        popoverSide={popoverSide}
        popoverSideOffset={popoverSideOffset}
        triggerClassName={triggerClassName}
      />
    );
  }

  return (
    <LineStylePickerContent
      value={props.value}
      onChange={(style) => {
        if (style.strokeStyle === "none") return;
        props.onChange({
          strokeStyle: style.strokeStyle,
          strokeWidth: style.strokeWidth,
        });
      }}
      styleOptions={LINE_STYLE_OPTIONS}
      onOpenChange={onOpenChange}
      tooltip={tooltip}
      popoverSide={popoverSide}
      popoverSideOffset={popoverSideOffset}
      triggerClassName={triggerClassName}
    />
  );
}
